<template>
  <v-layout justify-start column class="mb-4">
    <v-container fluid pa-4 ma-0>
      <case-header-new
        :hasDeletePermission="hasDeletePermission"
      >
      </case-header-new>
      <Loading :isLoading="isLoading">
        <v-layout row wrap mb-4>
          <v-flex sm12 md9 px-0 class="no-jump-to-column">
            <fieldset-container
              v-if="!isLoading"
              id="case-data-all"
              :caseData="currentCase"
              :caseType="caseType"
              v-on:fieldset-list-updated="fieldsetListUpdated"
              v-on:save="save()"
              :caseIsLoading="isLoading"
              :hasPermissionToSave="hasPermissionToSave"              
            ></fieldset-container>
          </v-flex>
          <v-flex
            md3
            px-2
            mb-0
            style="position: relative"
            class="no-jump-to-column hidden-sm-and-down"
            v-if="showJumpToMenu"
          >
            <v-flex xs12 px-0 class="rounded-3">
              <div class="caption opacity-54 mb-1 text-uppercase primary--text">
                <PageTitle pageTitle="Jump to a section"></PageTitle>
              </div>
              <v-divider class="mb-0"></v-divider>
            </v-flex>
            <case-menu
              id="case-edit-menu"
              v-if="!isLoading"
              :headers="orderedHeaders"
              v-on:save="save()"
              v-on:cancel="cancel()"
              :caseIsLoading="isLoading"
              :hasPermissionToSave="hasPermissionToSave"
            ></case-menu>
          </v-flex>
        </v-layout>
      </Loading>
    </v-container>
    <v-layout align-baseline xs12 md4 px-2 right>
      <v-snackbar v-model="showTaskResult" :bottom="true" :right="true">
        {{ taskResult.message }}
        <v-btn
          :color="taskResult.error ? 'error' : 'success'"
          flat
          @click="showTaskResult = false"
        >
          Close
        </v-btn>
      </v-snackbar>
    </v-layout>
    <invalid-fields-dialog></invalid-fields-dialog>
  </v-layout>
</template>

<script>
import Vue from "vue";
import PageTitle from "@/components/PageTitle.vue";
import FieldsetContainer from "@/components/cases/case-edit/FieldsetContainer.vue";
import CaseMenu from "@/components/cases/case-edit/CaseMenu";
import Loading from "@/components/shared/Loading.vue";
import CaseHeaderNew from "@/components/cases/case-edit/CaseHeaderNew.vue";
import InvalidFieldsDialog from "@/components/cases/case-edit/InvalidFieldsDialog.vue";
import { mapState } from "vuex";
import { clearInterval, setInterval } from "timers";
import Helpers from "@/helpers.js"

export default {
  name: "CaseEdit",
  data: () => ({ showTaskResult: false, orderedHeaders: [] }),
  props: { enableTimer: { type: Boolean, default: false } },
  mounted() {
    this.$store.dispatch(
      "setPageTitle",
      `${this.currentCase.caseType} - ${this.currentCase.title}`
    );
  },
  components: {
    Loading,
    PageTitle,
    FieldsetContainer,
    CaseMenu,
    CaseHeaderNew,
    InvalidFieldsDialog,
  },
  computed: {
    ...mapState({
      caseType: state => state.caseTypes.current,
      currentCase: (state) => state.cases.currentlySelected.case,
      currentFieldsets: state => state.cases.currentlySelected.case.schema.fieldsets,
      currentUser: (state) =>
        Object.assign(state.currentUser.me, {
          account: { id: state.currentUser.account.id },
        }),
      permissions: state => state.ui.grantedPermissions,
      caseTypeCode: state => state.caseTypes.current.code,
      isLoading: (state) => state.cases.currentlySelected.loading,
      currentCaseId: (state) => state.cbroute.params.case_id,
      currentViewers: (state) => state.cases.currentlySelected.viewedBy,
      taskRunning: (state) => state.currentUser.taskRunning,
      taskResult: (state) => state.currentUser.taskResult,
      showJumpToMenu: (state) => state.ui.keepMainMenuExpanded,
      hasDeletePermission() {
        if (this.currentUser.isAccountAdmin) { return true }
        return Helpers.hasPermission(this.permissions, "Delete", this.caseTypeCode)
      },
      hasPermissionToSave() {
        if (this.currentUser.isAccountAdmin) { return true }
        if (this.currentFieldsets && this.currentFieldsets.length > 0) {
          for (const fieldset of this.currentFieldsets) {
            for (const field of fieldset.fields) {
              if (!field.readonly) { 
                return true 
              } // Non-readonly field detected, allow update.
            }
          }
        }
        return false
      },
    }),
    pageTitle() {
      if (this.currentCase) {
        return this.currentCase.title;
      } else {
        return "Case Edit";
      }
    },
  },
  watch: {
    async currentCaseId() {
      if (this.currentCase) {
        await this.$store.dispatch("unsubscribeToCaseChannelV2", {
          caseId: this.currentCase.id,
          currentUser: this.currentUser,
        });
      }
      await this.$store.dispatch("subscribeToCaseChannelV2", {
        caseId: this.currentCaseId,
        currentUser: this.currentUser,
      });
      this.loadData();
    },
    currentCase() {
      this.loadData();
    },
    taskResult() {
      this.showTaskResult = this.taskResult !== undefined;
      setTimeout(() => {
        this.resetTaskResults();
      }, 6000);
    },
    enableTimer() {
      if (this.enableTimer) {
        this.timerIntervalID = setInterval(this.sayHello, 20 * 1000);
      }
    },
    pageTitle() {
      this.$store.dispatch(
        "setPageTitle",
        `${this.currentCase.caseType} - ${this.currentCase.title}`
      );
    },
  },
  methods: {
    fieldsetListUpdated({ fieldsetList }) {
      const headers = [];
      for (const row of fieldsetList || []) {
        for (const fs of row) {
          headers.push(fs);
        }
      }
      this.orderedHeaders = headers;
    },
    async loadData() {
      if (!this.currentCase || !this.currentCase.caseType) {
        await this.$store.dispatch("getCurrentlySelectedCase", {
          caseId: this.currentCaseId,
        });
        this.$store.dispatch(
          "setPageTitle",
          `${this.currentCase.caseType} - ${this.currentCase.title}`
        );
      }
    },
    save() {
      const caseData = {
        id: this.currentCaseId,
        title: this.currentCase.properties.title,
        currentState: this.currentCase.currentState,
        properties: this.currentCase.properties,
        checkVersion: this.currentCase.version,
      };
      this.$store.dispatch("updateCase", caseData);
    },
    resetTaskResults() {
      this.showTaskResult = false;
      if (this.shouldRefresh && !this.taskResult.error) {
        this.$router.back();
      }
      this.shouldRefresh = false;
    },
    cancel() {
      this.$router.back();
    },
    sayHello() {
      if (Vue.store) {
        const state = Vue.store.state;
        if (state && state.currentUser && state.currentUser.account) {
          const currentUser = Object.assign(state.currentUser.me, {
            account: { id: state.currentUser.account.id },
          });
          Vue.store.dispatch("subscribeToCaseChannelV2", {
            caseId: this.currentCase.id,
            currentUser,
          });
        }
      }
    },
  },
  destroyed() {
    if (this.timerIntervalID) {
      clearInterval(this.timerIntervalID);
    }
  },
  beforeRouteLeave(to, from, next) {
    const state = Vue.store.state;
    const currentUser = Object.assign(state.currentUser.me, {
      account: { id: state.currentUser.account.id },
    });
    Vue.store.dispatch("hideDocumentSidePanel");
    Vue.store.dispatch("unsubscribeToCaseChannelV2", {
      caseId: this.currentCase.id,
      currentUser: currentUser,
    });
    next();
  },
  beforeRouteEnter(to, from, next) {
    Vue.store.dispatch("hideDocumentSidePanel");
    Vue.store.dispatch("getCurrentlySelectedCase", {
      caseId: to.params.case_id,
    });
    const state = Vue.store.state;
    const currentUser = Object.assign(state.currentUser.me, {
      account: { id: state.currentUser.account.id },
    });
    Vue.store.dispatch("subscribeToCaseChannelV2", {
      caseId: to.params.case_id,
      currentUser,
    });
    next();
  },
  beforeRouteUpdate: (to, from, next) => {
    Vue.store.dispatch("hideDocumentSidePanel");
    Vue.store.dispatch("refreshRecentCases");
    Vue.store.dispatch("getCurrentlySelectedCase", {
      caseId: to.params.case_id,
    });
    next();
  },
};
</script>

<style>
.mini .jump-to-column {
  display: none;
}

.mini .no-jump-to-column {
  max-width: 100% !important;
  width: 100% !important;
  flex-basis: 100% !important;
}
</style>